import React, { useRef } from 'react';
import { Badge, Button, FloatButton, Tag, Tooltip, message as antMessage } from 'antd';
import {
  ArrowLeftOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  CustomerServiceOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { CATEGORY_LABEL, STATUS_LABEL } from '../../services/supportApi';
import {
  AttachSlotContext,
  AttachTarget,
  useFileDrop,
  usePreventWindowFileDrop,
} from './attach';
import NewTicketForm from './NewTicketForm';
import { useSupportPanel } from './SupportProvider';
import TicketList, { STATUS_COLOR } from './TicketList';
import TicketThread from './TicketThread';
import * as S from './styles';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BOTTOM_NAV_HEIGHT } from '../../constants/mobile';

/**
 * Floating "Support" button pinned bottom-right on every signed-in screen, opening a chat panel
 * where the user files a report, attaches screenshots or recordings, and reads admin replies
 * live. Mounted once in App so it survives route changes; state comes from SupportProvider so
 * the sidebar entry opens this same panel.
 */
const SupportWidget: React.FC = () => {
  const s = useSupportPanel();
  // The compact shell puts a tab bar where the button used to sit.
  const isMobile = useIsMobile();

  // Whichever composer is on screen claims this slot; a drop anywhere on the panel goes to it.
  const attachSlot = useRef<AttachTarget | null>(null);
  const canAttach =
    s.open && (s.view === 'new' || (s.view === 'thread' && s.active?.status !== 'CLOSED'));
  const { dragging, dropProps } = useFileDrop(
    (files) => attachSlot.current?.addFiles(files),
    canAttach,
  );
  // A near miss must not make the browser navigate to the dropped file and bin the session.
  usePreventWindowFileDrop(s.open);

  if (!s.available) return null;

  // Hiding is only offered where the sidebar entry is on screen; on a screen without one the
  // button is the only way in, so it stays put whatever the preference says.
  const canHideFab = s.hasOtherEntry;
  const showFab = !s.fabHidden || !s.hasOtherEntry;

  const hideFab = () => {
    s.setFabHidden(true);
    antMessage.info('The floating button is hidden. Open support from the sidebar instead.');
  };

  const title =
    s.view === 'new' ? 'New report' : s.view === 'thread' ? s.active?.subject ?? 'Ticket' : 'Support';

  return (
    <>
      {showFab && (
        <FloatButton
          type="primary"
          icon={<CustomerServiceOutlined />}
          tooltip={s.open ? undefined : 'Technical support'}
          badge={{ count: s.unread, overflowCount: 99 }}
          onClick={s.open ? s.closePanel : s.openPanel}
          style={{
            insetInlineEnd: isMobile ? 16 : 24,
            insetBlockEnd: isMobile ? BOTTOM_NAV_HEIGHT + 16 : 24,
            zIndex: S.PANEL_Z + 1,
          }}
          aria-label={`Technical support${s.unread ? `, ${s.unread} unread` : ''}`}
          data-testid="support-fab"
        />
      )}
      {s.open && (
        <AttachSlotContext.Provider value={attachSlot}>
          <S.Panel role="dialog" aria-label="Technical support" data-testid="support-panel" {...dropProps}>
            <S.Header>
              {s.view !== 'list' && (
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowLeftOutlined />}
                  onClick={s.showList}
                  aria-label="Back to tickets"
                />
              )}
              <S.HeaderTitle title={title}>
                {title}
                {s.view === 'list' && s.unread > 0 && (
                  <Badge count={s.unread} size="small" style={{ marginLeft: 8 }} />
                )}
                {s.view === 'thread' && s.active && (
                  <S.HeaderSub>
                    <Tag color={STATUS_COLOR[s.active.status]} style={{ marginInlineEnd: 6 }}>
                      {STATUS_LABEL[s.active.status]}
                    </Tag>
                    {CATEGORY_LABEL[s.active.category]}
                  </S.HeaderSub>
                )}
              </S.HeaderTitle>
              {canHideFab &&
                (s.fabHidden ? (
                  <Tooltip title="Show the floating button">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => s.setFabHidden(false)}
                      aria-label="Show the floating support button"
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Hide the floating button">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeInvisibleOutlined />}
                      onClick={hideFab}
                      aria-label="Hide the floating support button"
                    />
                  </Tooltip>
                ))}
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={s.closePanel}
                aria-label="Close support"
              />
            </S.Header>

            {s.view === 'list' && (
              <TicketList
                tickets={s.tickets}
                loading={s.listLoading}
                error={s.listError}
                onOpen={s.openTicket}
                onNew={s.showNew}
              />
            )}
            {s.view === 'new' && <NewTicketForm limits={s.limits} onCreate={s.createTicket} />}
            {s.view === 'thread' && (
              <TicketThread
                ticket={s.active}
                loading={s.activeLoading}
                limits={s.limits}
                onSend={s.sendMessage}
              />
            )}

            {dragging && (
              <S.DropOverlay data-testid="support-drop-overlay">
                <CloudUploadOutlined style={{ fontSize: 26 }} />
                Drop to attach
                <S.DropOverlayHint>
                  Images and videos, up to {s.limits.maxFilesPerMessage} files
                </S.DropOverlayHint>
              </S.DropOverlay>
            )}
          </S.Panel>
        </AttachSlotContext.Provider>
      )}
    </>
  );
};

export default SupportWidget;
